import * as React from 'react';
import { useState } from 'react';

import type { Locale } from 'date-fns';
import { addDays, format, isToday, startOfDay, subDays } from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { cn } from '../lib/utils';

import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export function DatePicker({
  date,
  buttonClassName,
  placeholderText = 'Pick a date',
  locale,
  showPreviousNextButtons = false,
  showTodayButton = false,
  todayButtonText = 'Back to Today',
  setDate,
}: {
  date?: Date;
  buttonClassName?: string;
  placeholderText?: string;
  locale?: Locale;
  showPreviousNextButtons?: boolean;
  showTodayButton?: boolean;
  todayButtonText?: string;
  setDate: (date: Date, source: 'PREV' | 'NEXT' | 'TODAY' | 'CALENDAR') => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const isTodaySelected = date && isToday(date);

  const handlePreviousDay = () => {
    if (date) {
      setDate(subDays(date, 1), 'PREV');
    }
  };

  const handleNextDay = () => {
    if (date) {
      setDate(addDays(date, 1), 'NEXT');
    }
  };

  const handleToday = () => setDate(startOfDay(new Date()), 'TODAY');

  const formatDisplayDate = (date: Date) =>
    format(date, 'MMM d, yyyy', { locale });

  return (
    <div className="vai-flex vai-items-center vai-gap-2">
      {/* Previous button */}
      {showPreviousNextButtons && (
        <Button
          variant="outline"
          onClick={handlePreviousDay}
          disabled={!date}
          className="vai-w-10 vai-p-0 vai-flex vai-items-center vai-justify-center"
        >
          <ChevronLeft className="vai-h-4 vai-w-4 vai-text-gray-600" />
        </Button>
      )}

      {/* Date display button with popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              showPreviousNextButtons || showTodayButton
                ? 'vai-flex vai-items-center vai-gap-2 vai-px-3 vai-py-2 vai-font-normal vai-w-[135px]'
                : 'vai-justify-start vai-text-left vai-font-normal vai-min-w-40',
              !date && 'vai-text-muted-foreground',
              buttonClassName,
            )}
          >
            <CalendarIcon
              className={cn(
                showPreviousNextButtons || showTodayButton
                  ? 'vai-h-4 vai-w-4 vai-text-gray-600'
                  : 'vai-mr-2 vai-h-4 vai-w-4',
              )}
            />
            <span>
              {date
                ? showPreviousNextButtons || showTodayButton
                  ? formatDisplayDate(date)
                  : format(date, 'PPP', { locale })
                : placeholderText}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="vai-w-auto vai-p-0">
          <Calendar
            defaultMonth={date}
            mode="single"
            selected={date}
            onSelect={(selectedDate) => {
              selectedDate && setDate(selectedDate, 'CALENDAR');
              setIsOpen(false);
            }}
            initialFocus
            locale={locale}
          />
        </PopoverContent>
      </Popover>

      {/* Next button */}
      {showPreviousNextButtons && (
        <Button
          variant="outline"
          onClick={handleNextDay}
          disabled={!date}
          className="vai-w-10 vai-p-0 vai-flex vai-items-center vai-justify-center"
        >
          <ChevronRight className="vai-h-4 vai-w-4 vai-text-gray-600" />
        </Button>
      )}

      {/* Today button */}
      {showTodayButton && !isTodaySelected && (
        <Button
          variant="outline"
          onClick={handleToday}
          className="vai-px-3 vai-py-2 vai-font-normal vai-ml-2"
        >
          {todayButtonText}
        </Button>
      )}
    </div>
  );
}
